import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Switch } from '@/common/ui/switch'
import { Card, CardContent } from '@/common/ui/card'
import { Alert, AlertDescription } from '@/common/ui/alert'
import { Info } from 'lucide-react'
import useMutationCreateOtpProvider from '@/modules/otp-provider/api/hooks/useMutationCreateOtpProvider'
import useMutationUpdateOtpProvider from '@/modules/otp-provider/api/hooks/useMutationUpdateOtpProvider'
import showErrorToast from '@/common/utils/showErrorToast'

const otpProviderSchema = z.object({
	name: z.string().min(1, 'กรุณาระบุชื่อผู้ให้บริการ'),
	otpKey: z.string().min(1, 'กรุณาระบุ OTP Key'),
	otpSecret: z.string().min(1, 'กรุณาระบุ OTP Secret'),
	isActive: z.boolean().default(true),
})

type OtpProviderFormData = z.infer<typeof otpProviderSchema>

interface OtpProviderFormProps {
	mode: 'create' | 'edit'
	providerId?: string
	initialData?: Partial<OtpProviderFormData>
	onSuccess?: () => void
}

export default function OtpProviderForm({
	mode,
	providerId,
	initialData,
	onSuccess,
}: OtpProviderFormProps) {
	const createMutation = useMutationCreateOtpProvider()
	const updateMutation = useMutationUpdateOtpProvider()

	const form = useForm<OtpProviderFormData>({
		resolver: zodResolver(otpProviderSchema),
		defaultValues: {
			name: '',
			otpKey: '',
			otpSecret: '',
			isActive: true,
		},
	})

	useEffect(() => {
		if (initialData && mode === 'edit') {
			form.reset({
				name: initialData.name || '',
				otpKey: initialData.otpKey || '',
				otpSecret: initialData.otpSecret || '',
				isActive: initialData.isActive ?? true,
			})
		}
	}, [initialData, mode, form])

	const onSubmit = async (data: OtpProviderFormData) => {
		try {
			if (mode === 'create') {
				await createMutation.mutateAsync({
					name: data.name,
					otpKey: data.otpKey,
					otpSecret: data.otpSecret,
				})
			} else if (mode === 'edit' && providerId) {
				await updateMutation.mutateAsync({
					id: providerId,
					body: {
						name: data.name,
						otpKey: data.otpKey,
						otpSecret: data.otpSecret,
						isActive: data.isActive,
					},
				})
			}
			onSuccess?.()
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	const isLoading = createMutation.isPending || updateMutation.isPending

	return (
		<Card>
			<CardContent className="p-6">
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>ชื่อผู้ให้บริการ</FormLabel>
									<FormControl>
										<Input
											{...field}
											placeholder="เช่น ThaiBulkSMS Production"
											disabled={isLoading}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="otpKey"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>OTP Key</FormLabel>
									<FormControl>
										<Input
											{...field}
											placeholder="เช่น 1828939416034155"
											disabled={isLoading}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="otpSecret"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>OTP Secret</FormLabel>
									<FormControl>
										<Input
											{...field}
											placeholder="เช่น 391e6c38df5c2a550d03f6af152f961a"
											disabled={isLoading}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						{mode === 'edit' && (
							<>
								<Alert>
									<Info className="h-4 w-4" />
									<AlertDescription>
										สามารถเปิดใช้งานผู้ให้บริการ OTP ได้เพียง 1 รายการเท่านั้น
										เมื่อเปิดใช้งานผู้ให้บริการรายนี้
										ระบบจะปิดการใช้งานผู้ให้บริการอื่นโดยอัตโนมัติ
									</AlertDescription>
								</Alert>

								<FormField
									control={form.control}
									name="isActive"
									render={({ field }) => (
										<FormItem className="flex items-center justify-between rounded-lg border p-4">
											<div className="space-y-0.5">
												<FormLabel>สถานะการใช้งาน</FormLabel>
												<div className="text-sm text-muted-foreground">
													เปิดหรือปิดการใช้งานผู้ให้บริการ OTP นี้
												</div>
											</div>
											<FormControl>
												<Switch
													checked={field.value}
													onCheckedChange={field.onChange}
													disabled={isLoading}
												/>
											</FormControl>
										</FormItem>
									)}
								/>
							</>
						)}

						<div className="flex gap-4">
							<Button type="submit" disabled={isLoading} className="flex-1">
								{isLoading
									? 'กำลังบันทึก...'
									: mode === 'create'
										? 'สร้างผู้ให้บริการ'
										: 'บันทึกการแก้ไข'}
							</Button>
						</div>
					</form>
				</Form>
			</CardContent>
		</Card>
	)
}
